import React, {useState} from 'react';

import {parse, differenceInDays} from 'date-fns';

import {useModalContext} from '@bootcamp/web/src/contexts/Modal';
import {Label2} from '@bootcamp/web/src/components/Typography/next';

import {RoundButton} from '@bootcamp/web/src/components/UI';

import CreateSchedule from './Create'
import {Checkmark} from '@styled-icons/fluentui-system-filled/Checkmark';
import {ArrowUturnLeft} from '@styled-icons/heroicons-solid/ArrowUturnLeft';
import {
  Container,
  Header,
  Header2Text,
  Wrapper,
  CloseIcon,
  WarningIcon,
  ConfirmationPanel,
  Left,
  CreateButton,
  Estimate,
  EstimateSubheading,
  Subheading,
  TimelineComponent,
  IconContent
} from './shared';

import styled from 'styled-components';

const Panel = styled(Left)`
  padding: 0px 12px;
`

const ConfirmationHeader = styled(Header)`
  padding: 0;
  border-bottom: none;
  margin-bottom: 8px;
  flex-direction: column;
`;
const ConfirmationSubheader = styled(Subheading)`
  font-size: 18px;
  margin-bottom: 32px;
`;
const ButtonArea = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  gap: 24px;
`;

const ScheduleLabel = styled(Label2)`
  color: #85868E;
  font-weight: 700;
  margin-bottom: 12px;
`;

const HeaderIconContent = styled(IconContent)`
  border-radius: 100%;
  background: ${({color, theme}) => color === 'red' ? theme.colors.interfacePalette.red.light : theme.colors.brandPalette.royal.light};
  color: ${({color, theme}) => color === 'red' ? theme.colors.interfacePalette.red.dark : theme.colors.brandPalette.royal.dark};
  margin-bottom: 8px;
`;

const RebalanceConfirmation = ({
  currentSchedule,
  hoursPerDay,
  startDate,
  examDate,
  onConfirm,
  onCancel,
  currentHoursPerDay,
  loading,
}) => {

  return (
    <Container id={"modal-container"} onClick={e => e.stopPropagation()}>
      <Wrapper>
      <ConfirmationPanel>
        <ConfirmationHeader>
          <HeaderIconContent><Checkmark size="20" /></HeaderIconContent>
          <CloseIcon onClick={onCancel} />
          <Header2Text>
            Your Rebalanced Schedule
          </Header2Text>
        </ConfirmationHeader>
        <ConfirmationSubheader>
          Check out what we’ve done to help you get back on track!
        </ConfirmationSubheader>
        <Wrapper>
          <Panel>
            <ScheduleLabel>
              Current Schedule
            </ScheduleLabel>
            <TimelineComponent
              startDate={currentSchedule.startDate}
              examDate={currentSchedule.examDate}
              hoursPerDay={currentHoursPerDay}
              expirationBeforeExamDate={currentSchedule.expirationBeforeExamDate}
              rebalance
              hideSubheading
            >
              {!!currentSchedule.overdueIncompleteEvents.length &&
                <Estimate color="red" hideSubheading>
                  <IconContent><WarningIcon/></IconContent>
                  <EstimateSubheading color="red">{currentSchedule.overdueIncompleteEvents.length} assignment{currentSchedule.overdueIncompleteEvents.length > 1 && `s`} overdue</EstimateSubheading>
                </Estimate>
              }
            </TimelineComponent>
          </Panel>
          <Panel>
            <ScheduleLabel>
              New Schedule
            </ScheduleLabel>
              <TimelineComponent
                startDate={startDate}
                examDate={examDate}
                hoursPerDay={hoursPerDay}
                expirationBeforeExamDate={currentSchedule.expirationBeforeExamDate}
                rebalance
                hideSubheading
              >
                <Estimate color="green" hideSubheading>
                  <IconContent color="royal"><Checkmark size="20" /></IconContent>
                  <EstimateSubheading color="green">No overdue assignments!</EstimateSubheading>
                </Estimate>
              </TimelineComponent>
          </Panel>
        </Wrapper>
        <ButtonArea>
          <RoundButton full white onClick={onCancel} style={{fontWeight: 400}}>
            Go back
          </RoundButton>
          <CreateButton full onClick={onConfirm} disabled={loading} color="royal">
            {loading ? 'Rebalancing...' : 'Looks great!'}
          </CreateButton>
        </ButtonArea>
      </ConfirmationPanel>
      </Wrapper>
    </Container>
  );
};

export const UndoRebalanceConfirmation = ({
  currentSchedule,
  originalSchedule,
  rollbackSchedule,
  onCancel,
  loading,
  close
}) => {
  return (
    <Container id={"modal-container"} onClick={e => e.stopPropagation()}>
      <Wrapper>
      <ConfirmationPanel>
        <ConfirmationHeader>
          <HeaderIconContent color="red"><ArrowUturnLeft size="20" /></HeaderIconContent>
          <CloseIcon onClick={close} />
          <Header2Text>
            Undo Rebalance
          </Header2Text>
        </ConfirmationHeader>
        <ConfirmationSubheader>
            Don’t like what you’re seeing? No worries, we can bring back your previous schedule
        </ConfirmationSubheader>
        <Wrapper>
          <Panel>
            <ScheduleLabel>
              Rebalanced Schedule
            </ScheduleLabel>
            <TimelineComponent
              startDate={currentSchedule.startDate}
              examDate={currentSchedule.examDate}
              hoursPerDay={currentSchedule.hoursPerDay}
              expirationBeforeExamDate={currentSchedule.expirationBeforeExamDate}
              rebalance
              hideSubheading
            >
              {!!currentSchedule.overdueIncompleteEvents.length &&
                <Estimate color="red" hideSubheading>
                  <IconContent><WarningIcon /></IconContent>
                  {currentSchedule.overdueIncompleteEvents.length} assignment{currentSchedule.overdueIncompleteEvents.length > 1 && `s`} overdue
                </Estimate>
              }
            </TimelineComponent>
          </Panel>
          <Panel>
            <ScheduleLabel>
              Previous Schedule
            </ScheduleLabel>
            <TimelineComponent
              startDate={originalSchedule.startDate}
              examDate={originalSchedule.examDate}
              hoursPerDay={originalSchedule.hoursPerDay}
              rebalance
              hideSubheading
              >
              {!!originalSchedule.overdueIncompleteEvents.length &&
                <Estimate color="red" hideSubheading>
                  <IconContent><WarningIcon /></IconContent>
                  {originalSchedule.overdueIncompleteEvents.length} assignment{originalSchedule.overdueIncompleteEvents.length > 0 && `s`} overdue
                </Estimate>
              }
            </TimelineComponent>
          </Panel>
        </Wrapper>
        <ButtonArea>
          <RoundButton full white onClick={close} style={{fontWeight: 400}}>
            Go back
          </RoundButton>
          <CreateButton full onClick={rollbackSchedule} disabled={loading} color="royal">
            {loading ? 'Undoing...' : 'Confirm Undo'}
          </CreateButton>
        </ButtonArea>
      </ConfirmationPanel>
      </Wrapper>
    </Container>
  );
};



const RebalanceModal = ({
  remainingHours,
  rebalanceSchedule,
  currentSchedule
}) => {
  const [loading, setLoading] = useState(false);
  const [rebalanceConfig, setRebalanceConfig] = useState(null);
  const [showingConfirmation, setShowingConfirmation] = useState(false);
  const {modalDispatch} = useModalContext();

  const currentDaysBetweenStartAndExam = currentSchedule.examDate && currentSchedule.startDate
    ? differenceInDays(parse(currentSchedule.examDate, 'yyyy-MM-dd', new Date()), parse(currentSchedule.startDate, 'yyyy-MM-dd', new Date())) - (currentSchedule?.daysOff?.length || 0)
    : null;

  const currentHoursPerDay = currentDaysBetweenStartAndExam ? remainingHours / currentDaysBetweenStartAndExam : null;

  const handleCreate = async (startDate, daysOff, examDate, hoursPerDay) => {
    setRebalanceConfig({startDate, daysOff, examDate, hoursPerDay});
    setShowingConfirmation(true);
  };

  const handleRebalance = async (startDate, daysOff, examDate, hoursPerDay) => {
    setLoading(true);

    try {
      await rebalanceSchedule(startDate, daysOff, examDate, hoursPerDay);
    } catch (error) {
      console.log('error rebalancing', error);
    }

    modalDispatch({type: 'close'});
  };

  const handleCancel = () => {
    setShowingConfirmation(false);
  };

  const handleClose = () => {
    modalDispatch({type: 'close'});
  };

  if (showingConfirmation) {
    return (
      <RebalanceConfirmation
        currentSchedule={currentSchedule}
        hoursPerDay={rebalanceConfig.hoursPerDay}
        startDate={rebalanceConfig.startDate}
        examDate={rebalanceConfig.examDate}
        currentHoursPerDay={currentHoursPerDay}
        onConfirm={handleRebalance}
        onCancel={handleCancel}
        onClose={handleClose}
        loading={loading}
      />
    );
  }

  return (
    <CreateSchedule
      onCreate={handleRebalance}
      totalHours={remainingHours}
      startingExamDate={currentSchedule.examDate}
      startingDaysOff={currentSchedule.daysOff}
      rebalance
    />
  );
};


export default RebalanceModal;